// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, type ReactNode } from "react";
import { WAKING_UP_MESSAGE } from "../components/common/warmUp/warmUpCopy";
import { useOnboardingStatus } from "../context/OnboardingContext";
import { navigateTo } from "../router/navigation";
import type { AppRoute } from "../types/routing";
import { isDevPreviewMode } from "../utils/devEnv";
import { FullPageLoader } from "./FullPageLoader";
import { WorkspaceLoadErrorScreen } from "./WorkspaceLoadErrorScreen";

interface OnboardingRedirectProps {
  route: AppRoute;
  children: ReactNode;
}

export function OnboardingRedirect({ route, children }: OnboardingRedirectProps) {
  const {
    isOnboarded,
    isOnboardingResolved,
    isOnboardingWakingUp,
    onboardingReadFailure,
    retryOnboardingRead,
  } = useOnboardingStatus();
  const mustOnboard =
    !isDevPreviewMode && isOnboardingResolved && !isOnboarded && route.key !== "onboarding";

  useEffect(() => {
    if (mustOnboard) {
      navigateTo("/onboarding");
    }
  }, [mustOnboard]);

  if (!isDevPreviewMode && onboardingReadFailure !== undefined) {
    return <WorkspaceLoadErrorScreen failure={onboardingReadFailure} onRetry={retryOnboardingRead} />;
  }

  if (!isDevPreviewMode && (!isOnboardingResolved || mustOnboard)) {
    return isOnboardingWakingUp ? <FullPageLoader message={WAKING_UP_MESSAGE} /> : <FullPageLoader />;
  }

  return <>{children}</>;
}
